/* eslint-disable max-len */
import { Injectable } from '@angular/core';
import { State, Selector, Action, StateContext, Store, Actions, ofAction, ofActionCompleted } from '@ngxs/store';
import { Activity, LenderUserActivityResponse } from 'app/models/dto/activity.model';
import { ActivitiesNextPage, GetLatestActivities, GetLenderUserActivities, GetMoreActivities, LatestActivitiesViewed } from './activity.actions';
import { ActivityService } from 'app/services/activity/activity.service';
import { PusherService } from 'app/modules/shared/services/pusher.service';
import { environment } from 'environments/environment';
import { lastValueFrom, Observable, of } from 'rxjs';
import { tap, take, catchError, filter, takeUntil } from 'rxjs/operators';
import { LenderUserState } from '../lender-user/lender-user/lender-user.state';
import { LenderUser } from 'app/models/dto/lender-user.model';
import { append, patch } from '@ngxs/store/operators';

export class ActivityStateModel {
  filterCriteria: {
    types: [];
    start: undefined;
    end: undefined;
    reset: boolean;
  };
  currentLenderUserActivityResponse: LenderUserActivityResponse;
  error: string;
  latestActivities: Activity[];
  latestActivitiesViewed: number;
  latestUnreadActivitiesCount: number;
  limit: number;
  loading: boolean;
  offset: number;
  nextPageLoading: boolean;
  noMoreNotifications: boolean;
}

// Interface with shape of arg data passed to getLenderUserActivities action.
export interface LenderUserActivitiesPayload {
  lenderUserId: number;
  queryFilters: string[];
  paginationPage?: number;
}
@Injectable()

@State<ActivityStateModel>({
  name: 'activity',
  defaults: {
    filterCriteria: {
      types: [],
      start: undefined,
      end: undefined,
      reset: true
    },
    latestActivities: [],
    latestActivitiesViewed: 0,
    latestUnreadActivitiesCount: 0,
    limit: 15,
    loading: false,
    offset: 0,
    nextPageLoading: false,
    noMoreNotifications: false,
    currentLenderUserActivityResponse: {
      data: [],
      links: [],
      meta: {
        current_page: 1,
        last_page: 1
      },
    },
    error: ''
  }
})

export class ActivityState {
  @Selector()
  static latestActivities(state: ActivityStateModel) {
    return state?.latestActivities ?? [];
  }

  @Selector()
  static latestActivitiesViewed(state: ActivityStateModel) {
    return state?.latestActivitiesViewed ?? 0;
  }

  @Selector()
  static latestUnreadActivitiesCount(state: ActivityStateModel) {
    return state?.latestUnreadActivitiesCount ?? 0;
  }

  @Selector()
  static getCurrentLenderUserActivities(state: ActivityStateModel) {
    return state?.currentLenderUserActivityResponse?.data;
  }

  @Selector()
  static getCurrentLenderUserActivityMeta(state: ActivityStateModel) {
    return state?.currentLenderUserActivityResponse?.meta;
  }

  @Selector()
  static loading(state: ActivityStateModel) {
    return state?.loading ?? false;
  }

  @Selector()
  static nextPageLoading(state: ActivityStateModel) {
    return state?.nextPageLoading ?? false;
  }

  @Selector()
  static noMoreNotifications(state: ActivityStateModel) {
    return state?.noMoreNotifications ?? false;
  }

  @Selector()
  static error(state: ActivityStateModel) {
    return state?.error ?? '';
  }

  lenderUser$: Observable<LenderUser | null>;

  interval: any;
  user: LenderUser | null;
  private pusherChannel: any;

  constructor(
    private activityService: ActivityService,
    private actions$: Actions,
    private store: Store,
    private pusherService: PusherService
  ) {
    this.lenderUser$ = this.store.select(LenderUserState.lenderUser);
  }

  @Action(GetLatestActivities)
  async getLatestActivities({ patchState, getState, setState }: StateContext<ActivityStateModel>, { criteria }: GetLatestActivities) {
    if (criteria.reset === true) {
      patchState({
        loading: true,
        noMoreNotifications: false,
        filterCriteria: {
          types: criteria.types,
          start: criteria.start,
          end: criteria.end,
          reset: criteria.reset
        }
      });
    }
    const { filterCriteria } = getState();
    criteria = filterCriteria;
    this.pusherSubscribe();
    this.lenderUser$.pipe(filter((u): u is LenderUser => !!u), take(1)).subscribe({
      next: currentUser => {
        this.user = currentUser;
      },
      error: (error) => {
        console.error('Error in lenderUser subscription for activity:', error);
      }
    });
    const limit = 15;
    const offset = 0;

    const latestActivities = await lastValueFrom(this.activityService.getLatestActivities({ limit, offset, criteria })
      .pipe(takeUntil(this.actions$.pipe(ofActionCompleted(GetLatestActivities)))), { defaultValue: [] }
    );

    const latestActivitiesViewed =
      getState().latestActivitiesViewed
      || (this.user && this.user.settings && this.user.settings.lastViewedNotificationTimestamp)
      || 0;

    let filteredActivities = latestActivities.filter((activity: Activity) => activity.createdTimestamp * 1000 > latestActivitiesViewed);

    // Let's filter for saved notification-history activity items
    const notificationTypesStorage = this.getLocalStorageNotificationTypes();
    if (notificationTypesStorage.length > 0) {
      filteredActivities = filteredActivities.filter((activity: Activity) => notificationTypesStorage?.includes(activity.type));
    }

    setState(
      patch({
        latestActivities,
        latestActivitiesViewed,
        latestUnreadActivitiesCount: filteredActivities.length,
        limit,
        offset,
        noMoreNotifications: false,
        loading: false
      })
    );
  }

  @Action(GetMoreActivities)
  getMoreActivities({ getState, setState, patchState }: StateContext<ActivityStateModel>, { criteria }: GetMoreActivities) {
    const { limit, offset, latestActivities } = getState();
    if (latestActivities.length > offset) {
      return;
    }
    patchState({ loading: true, noMoreNotifications: false });
    const latestActivitiesViewed =
      getState().latestActivitiesViewed
      || (this.user && this.user.settings && this.user.settings.lastViewedNotificationTimestamp)
      || 0;
    return this.activityService.getMoreActivities({
      limit,
      offset,
      criteria
    }).pipe(
      catchError( error => {
        console.error(error);
        return of();
      })
    )
      .subscribe({
      next: items => {
        if (!items.length) {
          patchState({
            noMoreNotifications: true,
            loading: false,
            nextPageLoading: false
          });
        }

        if (items.length) {
          setState(
            patch({
              latestActivities: append(items),
              latestActivitiesViewed,
              loading: false,
              nextPageLoading: false,
              noMoreNotifications: false
            })
          );
        }
      }
    });
  }

  @Action(LatestActivitiesViewed)
  async handleLatestActivitiesViewed({ patchState }: StateContext<ActivityStateModel>, _: LatestActivitiesViewed) {
    this.lenderUser$.pipe(filter((u): u is LenderUser => !!u), take(1)).subscribe({
      next: currentUser => {
        this.user = currentUser;
      },
      error: (error) => {
        console.error('Error in lenderUser subscription for handleLatestActivitiesViewed:', error);
      }
    });

    await lastValueFrom(this.activityService.markLatestActivitiesViewed(this.user?.user.id).pipe(
      takeUntil(this.actions$.pipe(ofAction(LatestActivitiesViewed)))
    ), { defaultValue: 0 });

    patchState({
      latestActivitiesViewed: Date.now(),
      latestUnreadActivitiesCount: 0
    });
  }

  @Action(GetLenderUserActivities, { cancelUncompleted: true })
  getLenderUserActivities({ patchState, getState }: StateContext<ActivityStateModel>,
    payload: LenderUserActivitiesPayload) {

    const state = getState();
    return this.activityService.getLenderUserActivities(payload.lenderUserId,
      payload.queryFilters, payload.paginationPage).pipe(
        tap(response => {
          // Only load more button (page > 1) should append activities.
          let newActivities;
          if (payload?.paginationPage && payload.paginationPage > 1) {
            newActivities = [...state?.currentLenderUserActivityResponse?.data, ...response.data];
          } else {
            newActivities = response.data;
          }
          patchState({
            currentLenderUserActivityResponse: {
              // Add new activities to existing for this user.
              data: newActivities,
              links: response.links,
              // Using snake case provided by response.
              meta: {
                current_page: response?.meta?.current_page ?? 1,
                last_page: response?.meta?.last_page ?? 1
              }
            }
          });
        }), catchError(() => {
          patchState({
            currentLenderUserActivityResponse: {
              data: [],
              links: [],
              meta: {
                current_page: 1,
                last_page: 1
              }
            },
            error: 'Something went wrong. Please try again.'
          });
          return of();
        })
      );
  }

  @Action(ActivitiesNextPage)
  async handleActivitiesNextPage({ getState, patchState }: StateContext<ActivityStateModel>, { criteria }: ActivitiesNextPage) {
    const { limit, offset } = getState();
    patchState({
      offset: offset + limit,
      nextPageLoading: true
    });
    this.store.dispatch(new GetMoreActivities(criteria));
  }

  private async pusherSubscribe() {
    if (this.pusherChannel) {
      return;
    }
    const env = environment.environment;
    this.lenderUser$.pipe(filter((u): u is LenderUser => !!u), take(1)).subscribe({
      next: currentUser => {
        this.user = currentUser;
        if (this.pusherChannel) {
          return;
        }
        this.pusherChannel = this.pusherService.subscribeRaw(`private-lender-user-${currentUser.id}-${env}-notification-channel`);
        this.pusherChannel.bind_global(this.handlePusherMessage.bind(this));
      },
      error: (error) => {
        console.error('Error in lenderUser subscription for pusherSubscribe:', error);
      }
    });
  }

  private handlePusherMessage(eventName: string, data: any) {
    const relevant = (
      (eventName === 'create' && data && data.resourceName === 'activity')
    );
    if (!relevant) {
      return;
    }
    const criteria = {
      types: [] = [],
      start: undefined,
      end: undefined,
      reset: false
    };
    this.store.dispatch(new GetLatestActivities(criteria));
  }

  private getLocalStorageNotificationTypes(): string[] {
    return JSON.parse(localStorage.getItem('savedNotificationTypes') ?? '[]');
  }
}
